import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MatchGrid from '../components/MatchGrid';
import MatchContinuationOverlay from '../components/MatchContinuationOverlay';
import ConfirmationOverlay from '../components/ConfirmationOverlay'; // Import ConfirmationOverlay
import { handleAuthError } from '../utils/auth';

const HistoryPage = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // New state for delete confirmation
  const [matchToDelete, setMatchToDelete] = useState(null); // New state to store match to be deleted
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // New state for success messages
  // const { user } = useAuth(); // AuthContext does not exist, directly use localStorage for token

  const [selectedMatchForContinuation, setSelectedMatchForContinuation] = useState(null);
  const [isContinuationOverlayOpen, setIsContinuationOverlayOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMatchHistory = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      try {
        const response = await fetch('/best3intennis-api/api/get_match_history.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
          }
          throw new Error(data?.message || `HTTP error! status: ${response.status}`);
        }

        if (data.success) {
          setMatches(data.matches);
        } else {
          if (data.message?.includes('token') || data.error?.includes('token')) {
            handleAuthError(navigate);
          } else {
            setError(data.message || 'Failed to fetch match history.');
          }
        }
      } catch (e) {
        if (e.message.includes('token')) {
          handleAuthError(navigate);
        } else {
          setError(e.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMatchHistory();
  }, []);

  const handleSelectMatch = (match) => {
    if (match.status === 'inprogress') { // Corrected status check
      setSelectedMatchForContinuation(match);
      setIsContinuationOverlayOpen(true);
    } else {
      navigate(`/match-details/${match.match_id}`); // Navigate to match details page for completed matches
    }
  };

  const handleConfirmContinuation = (matchId) => {
    setIsContinuationOverlayOpen(false);
    setSelectedMatchForContinuation(null);
    navigate(`/match-tracker/${matchId}`, { state: { isContinuedMatch: true } }); // Navigate to MatchTrackerPage with match ID and state
  };

  const handleCancelContinuation = () => {
    setIsContinuationOverlayOpen(false);
    setSelectedMatchForContinuation(null);
  };

  const handleDeleteMatchClick = (match) => {
    setMatchToDelete(match);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    if (!matchToDelete) return;

    const token = localStorage.getItem('token');
    if (!token) {
      handleAuthError(navigate);
      return;
    }

    try {
      const response = await fetch('/best3intennis-api/api/delete_match.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ match_id: matchToDelete.match_id }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        setMatches(prevMatches => prevMatches.filter(match => match.match_id !== matchToDelete.match_id));
        setMatchToDelete(null);
        setSuccessMessage(data.message); // Set success message
        setError(null); // Clear any previous errors
      } else {
        setError(data.message || 'Failed to delete match.');
        setSuccessMessage(null); // Clear any previous success messages
      }
    } catch (e) {
      setError(e.message);
      setSuccessMessage(null); // Clear any previous success messages
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setMatchToDelete(null);
  };

  if (loading) {
    return <div className="text-center py-8">Loading match history...</div>;
  }

  if (error) {
    // Error is now handled by redirecting, but we can keep this for other errors
    return <div className="text-center py-8 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      {successMessage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-green-500 p-6 rounded-lg shadow-lg text-white max-w-sm mx-auto text-center">
            <p className="text-lg font-semibold mb-4">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="px-4 py-2 bg-white text-green-800 rounded hover:bg-gray-200 focus:outline-none"
            >
              OK
            </button>
          </div>
        </div>
      )}
      <h1 className="text-3xl font-bold mb-6 text-center">Match History</h1>
      {matches.length === 0 ? (
        <p className="text-center text-gray-600">No matches played yet.</p>
      ) : (
        <MatchGrid matches={matches} onSelectMatch={handleSelectMatch} onDeleteMatch={handleDeleteMatchClick} />
      )}

      {isContinuationOverlayOpen && (
        <MatchContinuationOverlay
          match={selectedMatchForContinuation}
          onConfirm={handleConfirmContinuation}
          onCancel={handleCancelContinuation}
        />
      )}

      {showDeleteConfirmation && (
        <ConfirmationOverlay
          message={`Are you sure you want to delete the match against ${matchToDelete?.opponent_name} on ${new Date(matchToDelete?.match_date).toLocaleDateString()}? This action cannot be undone.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default HistoryPage;


